import React, { memo } from 'react';
import { Button, Dropdown, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ApprovalWorkflowItemDto } from '@api/Api';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as DeleteSvg } from '@assets/icons/interviews-delete-user.svg';
import { ReactComponent as ScissorsSvg } from '@assets/icons/scissors.svg';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import {
  QUERY_APPROVAL_APPROVAL_WORKFLOW,
  useDeleteApprovalWorkflow,
  useUpdateApprovalWorkflow
} from '@hooks/queries/approval';
import { useCurrentWorkspace } from '@hooks/workspace';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import { useOrganization } from '@components/OrganizationBoundary';

type NameCellProps = {
  approval: ApprovalWorkflowItemDto;
  assetId: string;
};

export default memo(function ActionCell({ approval, assetId }: NameCellProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const { currentOrganization } = useOrganization();
  const canCreateAw = !!currentWorkspace?.permissions.createApprovalWorkflows;
  const awFeatureEnabled =
    !!currentOrganization.entity?.features.approvalRouting;
  const isDisabled = !approval.permissions.manage;

  const queryKey = [QUERY_APPROVAL_APPROVAL_WORKFLOW];
  const {
    mutateAsync: updateApprovalWorkflow,
    isPending: isLoadingUpdateApprovalWorkflow
  } = useUpdateApprovalWorkflow({
    queryKey
  });

  const {
    mutateAsync: deleteApprovalWorkflow,
    isPending: isLoadingDeleteApprovalWorkflow
  } = useDeleteApprovalWorkflow({});

  const isLoading =
    isLoadingUpdateApprovalWorkflow || isLoadingDeleteApprovalWorkflow;

  const onDelete = () => {
    if (isLoading) return;
    const isDeleteApproval = approval.assets.length === 1;
    Modal.confirm({
      focusTriggerAfterClose: true,
      zIndex: 1010,
      width: 474,
      icon: null,
      centered: true,
      closable: true,
      maskClosable: true,
      okText: 'Yes',
      cancelText: 'No',
      okButtonProps: {
        style: { minWidth: 124, height: 48, padding: '8px 15px' }
      },
      cancelButtonProps: {
        style: {
          marginRight: '16px',
          border: 'none',
          height: 48,
          padding: '8px 32px'
        }
      },
      onOk: async () => {
        if (isDeleteApproval) {
          await deleteApprovalWorkflow({
            id: approval.id
          });
          navigate(`/campaigns/single/${approval.campaignId}?tab=approvals`);
          return;
        }
        await updateApprovalWorkflow({
          id: approval.id,
          assets: {
            remove: [assetId]
          }
        });
      },
      closeIcon: <CrossSvg />,
      content: (
        <div className="modal_container">
          <h2
            className="modal_container__title"
            style={{ fontSize: 22, marginBottom: '0.5em' }}
          >
            Delete media
          </h2>
          {!isDeleteApproval && (
            <p
              className="modal_container__subtitle"
              style={{ marginBottom: '48px' }}
            >
              Are you sure you want to delete media?
            </p>
          )}
          {isDeleteApproval && (
            <p
              className="modal_container__subtitle"
              style={{ marginBottom: '48px' }}
            >
              You’re about to delete all media in{' '}
              <strong>{approval.name}</strong> approval routing. This will also
              remove the approval, including all its stages and data. Are you
              sure you want to proceed?
            </p>
          )}
        </div>
      )
    });
  };

  const onSplit = () => {
    if (!awFeatureEnabled) return;
    dispatch(
      toggleApprovalModal({
        visible: true,
        permissions: {
          create: canCreateAw
        },
        campaignId: approval.campaignId,
        approvalWorkflowSplit: {
          ...approval,
          assets: approval.assets.filter((asset) => asset.id === assetId)
        }
      })
    );
  };

  return (
    <Dropdown
      trigger={['hover']}
      disabled={isDisabled}
      menu={{
        className: 'campaign-approval-table-menu-overlay',
        multiple: false,
        items: [
          ...(awFeatureEnabled
            ? [
                {
                  key: 'split',
                  label: 'Split into a new approval',
                  icon: <ScissorsSvg />,
                  onClick: onSplit
                }
              ]
            : []),
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteSvg />,
            onClick: onDelete
          }
        ],
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation();
        }
      }}
    >
      <Button
        type="text"
        disabled={isDisabled}
        style={{ width: 24, height: 24, padding: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ActionSvg style={{ marginLeft: 0, marginRight: 0 }} />
      </Button>
    </Dropdown>
  );
});
